import type { ItemType } from '@/types/pricebook';
import type { DiscountTier, DiscountType, MembershipPlan } from './membership-plan-shared';

/** A single row in the membership pricing breakdown for an item. */
export interface MembershipPricingRow {
  planId: string;
  planName: string;
  /** The discount tier (if any) within the plan that matched this item type. */
  tierLabel: string | null;
  /** Display string for the discount, e.g. "20% off" or "$10 off" or "Set $80". */
  discountLabel: string;
  /** Final unit price after applying the matched discount. `null` when undefined input. */
  finalPrice: number | null;
  /** Plan applies to this item type (eligibility check passed). */
  appliesToType: boolean;
  /** The item type matched at least one of the plan's discount tiers. */
  matchedTier: boolean;
}

/**
 * Item types that can carry a member-pricing breakdown. A membership's price is
 * defined elsewhere (on the linked plan), so it never surfaces a breakdown —
 * matching the pricebook item forms, which only mount
 * MembershipPricingBreakdown for billable items.
 *
 * A FEE belongs here even though a plan usually WAIVES it outright: a waiver is
 * all-or-nothing, and "members pay $29 for the trip charge instead of $89" is a
 * member price, not a waiver. Leaving fees out would make the discounted-fee
 * plan unrepresentable while the free-fee one worked.
 */
const MEMBER_PRICING_ITEM_TYPES = new Set<ItemType>(['service', 'material', 'equipment', 'fee']);

/**
 * Normalize an arbitrary item-type string to the ItemType eligible for a
 * member-pricing breakdown, or `null` when the type is membership or unknown.
 */
export function toMemberPricingItemType(type: string | null | undefined): ItemType | null {
  const normalized = String(type ?? '').toLowerCase() as ItemType;
  return MEMBER_PRICING_ITEM_TYPES.has(normalized) ? normalized : null;
}

const ITEM_TYPE_TO_TIER_APPLIES_TO: Record<ItemType, string> = {
  service: 'SERVICE',
  material: 'MATERIAL',
  equipment: 'EQUIPMENT',
  bundle: 'BUNDLE',
  membership: 'MEMBERSHIP',
  fee: 'FEE',
};

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

function formatDiscountLabel(type: DiscountType, value: number): string {
  if (type === 'PERCENT') return `${value}% off`;
  if (type === 'FLAT') return `$${value.toFixed(2)} off`;
  return `Set $${value.toFixed(2)}`;
}

function applyDiscount(basePrice: number, type: DiscountType, value: number): number {
  if (type === 'PERCENT') {
    const adj = basePrice * (1 - value / 100);
    return Math.max(0, round2(adj));
  }
  if (type === 'FLAT') {
    return Math.max(0, round2(basePrice - value));
  }
  // SET_PRICE
  return Math.max(0, round2(value));
}

/**
 * For a given item type, find the tier within a plan whose `appliesTo` includes
 * the type. Returns null if no tier matches.
 */
function findMatchingTier(
  itemType: ItemType,
  itemId: string | undefined,
  tiers: DiscountTier[] | undefined,
): DiscountTier | null {
  if (!tiers?.length) return null;
  const wantedKey = ITEM_TYPE_TO_TIER_APPLIES_TO[itemType];
  return (
    tiers.find((tier) => {
      const specificItemIds = (tier as { specificItemIds?: string[] }).specificItemIds ?? [];
      if (specificItemIds.length > 0) {
        return Boolean(itemId && specificItemIds.includes(itemId));
      }
      const appliesTo = tier.appliesTo ?? [];
      return appliesTo.length === 0
        ? true
        : appliesTo.some((entry) => entry.toUpperCase() === wantedKey);
    }) ?? null
  );
}

/**
 * Returns true when the plan's eligibility rules don't exclude this item.
 *
 * If `eligibleLineItemTypes` is non-empty, the type must be listed.
 * `eligibleLineItemIds` is treated as an allowlist when non-empty.
 * `excludedLineItemIds` always excludes.
 */
function isItemEligibleForPlan(
  plan: MembershipPlan,
  itemType: ItemType,
  itemId: string | undefined,
): boolean {
  const types = plan.eligibleLineItemTypes ?? [];
  if (types.length > 0) {
    const upperType = ITEM_TYPE_TO_TIER_APPLIES_TO[itemType];
    const allowedTypeMatch = types.some((t) => t.toUpperCase() === upperType);
    if (!allowedTypeMatch) return false;
  }
  if (itemId) {
    if ((plan.excludedLineItemIds ?? []).includes(itemId)) return false;
    const allowIds = plan.eligibleLineItemIds ?? [];
    if (allowIds.length > 0 && !allowIds.includes(itemId)) return false;
  }
  return true;
}

/**
 * Compute the membership pricing breakdown for a non-membership pricebook item.
 *
 * Eligible plans are surfaced even when the base price is unknown/zero — the row
 * still renders with `finalPrice: null` so callers can fall back to "—" instead
 * of silently dropping the row (NOT-54).
 */
export function buildMembershipPricingBreakdown(input: {
  basePrice: number | null | undefined;
  itemType: ItemType;
  itemId?: string;
  plans: MembershipPlan[];
  /** Per-item absolute member unit price. Wins over any plan discount/tier. */
  memberPrice?: number | null;
  /** Per-item member discount %. Final fallback when the plan gives nothing. */
  memberDiscountPercent?: number | null;
}): MembershipPricingRow[] {
  const { basePrice, itemType, itemId, plans, memberPrice, memberDiscountPercent } = input;
  const priceIsValid =
    basePrice != null && Number.isFinite(Number(basePrice)) && Number(basePrice) >= 0;
  const price = priceIsValid ? Number(basePrice) : 0;
  // NOT-605 precedence (mirrors membership-discount.service): an explicit per-item
  // member PRICE wins over plan discounts/tiers; a per-item member % is the final
  // fallback used only when the plan itself gives no discount.
  const itemMemberPrice =
    memberPrice != null && Number.isFinite(Number(memberPrice)) ? Number(memberPrice) : null;
  const itemMemberPct =
    memberDiscountPercent != null && Number(memberDiscountPercent) > 0
      ? Number(memberDiscountPercent)
      : 0;

  return plans
    .map((plan) => {
      // Plan-level eligibility wins for every plan, tiered or not. An item the
      // plan excludes (by eligibleLineItemTypes / eligibleLineItemIds /
      // excludedLineItemIds) must never surface a discount — a matching tier is
      // only evaluated once the plan itself covers the item, so we don't quote a
      // discount the customer would not actually receive.
      const baseEligible = isItemEligibleForPlan(plan, itemType, itemId);
      if (!baseEligible) return null;

      const base = { planId: plan.id ?? '', planName: plan.name, appliesToType: true } as const;

      // 1. A per-item member price wins over any plan discount — quote it for
      //    every eligible plan so the preview matches what the member pays.
      if (itemMemberPrice != null && itemMemberPrice < price) {
        return {
          ...base,
          tierLabel: null,
          discountLabel: 'Member price',
          finalPrice: priceIsValid ? round2(itemMemberPrice) : null,
          matchedTier: false,
        };
      }

      // 2. Per-item member % is the final fallback used when the plan gives no
      //    discount of its own.
      const memberPctRow = (): MembershipPricingRow => ({
        ...base,
        tierLabel: null,
        discountLabel: formatDiscountLabel('PERCENT', itemMemberPct),
        finalPrice: priceIsValid ? applyDiscount(price, 'PERCENT', itemMemberPct) : null,
        matchedTier: false,
      });

      const hasTiers = (plan.discountTiers ?? []).length > 0;
      const matchingTier = findMatchingTier(itemType, itemId, plan.discountTiers);
      const planDiscount = Number(plan.discountPercent) || 0;

      if (hasTiers) {
        const hasAnyDiscount = matchingTier != null;
        if (!hasAnyDiscount && itemMemberPct > 0) return memberPctRow();
        return {
          ...base,
          tierLabel: matchingTier?.label?.trim() || null,
          discountLabel: hasAnyDiscount
            ? formatDiscountLabel(matchingTier.type, matchingTier.value)
            : 'No discount',
          finalPrice: priceIsValid
            ? hasAnyDiscount
              ? applyDiscount(price, matchingTier.type, matchingTier.value)
              : round2(price)
            : null,
          matchedTier: Boolean(matchingTier),
        };
      }

      if (planDiscount <= 0) {
        if (itemMemberPct > 0) return memberPctRow();
        return {
          ...base,
          tierLabel: null,
          discountLabel: 'No discount',
          finalPrice: priceIsValid ? round2(price) : null,
          matchedTier: false,
        };
      }

      return {
        ...base,
        tierLabel: null,
        discountLabel: formatDiscountLabel('PERCENT', planDiscount),
        finalPrice: priceIsValid ? applyDiscount(price, 'PERCENT', planDiscount) : null,
        matchedTier: false,
      };
    })
    .filter((row): row is MembershipPricingRow => row !== null);
}
